import {
  AiIcon,
  Button,
  CircularProgress,
  DocumentIcon,
  Stack,
  Tag,
  Typography,
} from '@all-manager/design-system-v2';
import { useContractAiAnalyzing } from './useContractAiAnalyzing';

const AI_PRIMARY_COLOR = '#651AC0';
const AI_SURFACE_BORDER = 'rgba(101, 26, 192, 0.22)';
const AI_SURFACE_BG = 'linear-gradient(180deg, rgba(101, 26, 192, 0.05) 0%, rgba(101, 26, 192, 0.015) 100%)';
const AI_HALO_BG = 'rgba(101, 26, 192, 0.1)';
const AI_HALO_BORDER = 'rgba(101, 26, 192, 0.25)';
const AI_HALO_SHADOW = '0 0 24px rgba(101, 26, 192, 0.16)';
const AI_PILL_BG = 'rgba(101, 26, 192, 0.08)';
const AI_PILL_TEXT = '#4A128B';

type Props = {
  fileName: string;
  onCancel: () => void;
};

export default function ContractAiAnalyzing(props: Props) {
  const hook = useContractAiAnalyzing();

  // Truncate long file names to avoid layout overflow while keeping extension
  const displayName = props.fileName.length > 50
    ? `${props.fileName.slice(0, 35)}…${props.fileName.slice(-10)}`
    : props.fileName;

  return (
    <div role="status" aria-live="polite">
      <div
        style={{
          background: AI_SURFACE_BG,
          border: `1px solid ${AI_SURFACE_BORDER}`,
          borderRadius: 12,
          padding: '32px 20px',
        }}
      >
        <Stack spacing={2} alignItems="center">
          {/* Circular AI glowing beacon */}
          <div
            style={{
              position: 'relative',
              width: 68,
              height: 68,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Halo background */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                backgroundColor: AI_HALO_BG,
                border: `1px solid ${AI_HALO_BORDER}`,
                boxShadow: AI_HALO_SHADOW,
              }}
            />
            {/* AI icon centered */}
            <div
              style={{
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: AI_PRIMARY_COLOR,
              }}
            >
              <AiIcon fontSize="large" />
            </div>
            {/* Circular progress ring framing the beacon */}
            <div
              style={{
                position: 'absolute',
                inset: -2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: AI_PRIMARY_COLOR,
              }}
            >
              <CircularProgress
                size="large"
                color="inherit"
                aria-label="Analyse IA en cours"
              />
            </div>
          </div>

          {/* AI Status tag */}
          <Tag color="ai" icon={<AiIcon />} label="Analyse IA en cours" />

          {/* Step title & rotating step indicators */}
          <Stack spacing={1} alignItems="center">
            <Typography variant="h6">
              {hook.currentStepMessage}
            </Typography>

            {/* Stepper pill dots */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
              {Array.from({ length: hook.totalSteps }).map((_, index) => {
                const isActive = index === hook.currentStepIndex;
                return (
                  <span
                    key={index}
                    style={{
                      height: 6,
                      width: isActive ? 18 : 6,
                      borderRadius: 3,
                      backgroundColor: isActive ? AI_PRIMARY_COLOR : 'rgba(101, 26, 192, 0.25)',
                      transition: 'all 0.3s ease',
                    }}
                  />
                );
              })}
            </div>
          </Stack>

          {/* Document indicator pill */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              maxWidth: '100%',
              padding: '6px 12px',
              borderRadius: 6,
              backgroundColor: AI_PILL_BG,
              color: AI_PILL_TEXT,
              fontSize: '0.8125rem',
              fontWeight: 500,
            }}
          >
            <DocumentIcon fontSize="small" />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName}
            </span>
          </div>

          <Typography variant="body2">
            Notre modèle analyse les clauses, options et cases cochées pour pré-remplir le contrat.
          </Typography>

          {/* Cancel button */}
          <Button
            variant="text"
            size="small"
            color="default"
            onClick={props.onCancel}
          >
            Annuler et remplir manuellement
          </Button>
        </Stack>
      </div>
    </div>
  );
}
